/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DataTable from "examples/Tables/DataTable";

//React Hooks and Others
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import Swal from "sweetalert2";
import {
  ENTIDAD_EDIT_URL,
  ENTIDAD_NEW_URL,
  ENTIDADES_URL,
} from "config/bases_url";
import PropTypes from "prop-types";
import {
  useDeleteEntidadMutation,
  useGetEntidadesQuery,
  useLazyGetEntidadByIdQuery,
} from "services/administracion/entidades.service";
import { CircularProgress, Icon, Tooltip } from "@mui/material";
import { setEditAdmin, setNoHereAdmin } from "slices";
import ModalEntidadShow from "components/ModalShow/modal_entidad.show";
import { style_icon } from "helpers/global_vars";
import { useRedirectForm } from "hooks/useRedirectForm";
import useModalHandler from "../../../../hooks/useModalHandler";
import usePagination from "hooks/usePagination";

function DataTableEntidades({ tableHeader }) {
  const {
    open: openShow,
    handleOpen: handleOpenShow,
    handleClose: handleCloseShow,
  } = useModalHandler();

  const {
    offset,
    setOffset,
    limit,
    busqueda,
    setBusqueda,
    handleNext,
    handlePrevious,
  } = usePagination();

  const dispatch = useDispatch();

  const {
    data: entidades,
    isLoading: isLoadingG,
    isError: isErrorG,
    error: errorG,
  } = useGetEntidadesQuery(
    {
      filter_params: {
        buscar: busqueda,
        offset: offset,
        limit: limit,
        query:
          "{id,nombre,municipio,localidad,clave_del_centro,dominio_de_correo,logo}",
      },
      shape: {
        id: "id",
        nombre: "nombre",
        municipio: "municipio",
        localidad: "localidad",
        clave_del_centro: "clave_del_centro",
        dominio_de_correo: "dominio_de_correo",
        logo: "logo",
      },
    },
    {
      refetchOnReconnect: true,
    }
  );

  //constantes para manejar la paginacion
  const dataEntidades = entidades?.results ? entidades.results : entidades;
  const countTotal = entidades?.count;
  const previous = entidades?.previous;
  const next = entidades?.next;

  const [
    deleteEntidad,
    {
      isSuccess: isSuccessD,
      isLoading: isLoadingD,
      isError: isErrorD,
      error: errorD,
    },
  ] = useDeleteEntidadMutation();

  const [
    getEntidadById,
    {
      data: entidad,
      isLoading: isLoadingGId,
      isError: isErrorGId,
      error: errorGId,
    },
  ] = useLazyGetEntidadByIdQuery();

  const dataTable = {
    columns: [
      {
        Header: "Nombre",
        accessor: "nombre",
      },
      {
        Header: "Municipio",
        accessor: "municipio",
      },
      {
        Header: "Localidad",
        accessor: "localidad",
      },
      {
        Header: "Clave del Centro",
        accessor: "clave_del_centro",
      },
      {
        Header: "Acciones",
        accessor: "actions",
        width: "1%",
        Cell: ({ row }) => {
          const { original } = row;

          return (
            <MDBox display="flex">
              <MDBox px={0.2}>
                <Tooltip placement="top" title="Ver detalles">
                  <Icon
                    style={style_icon}
                    onClick={() => handleClickMore(original)}
                    fontSize="small"
                  >
                    find_in_page
                  </Icon>
                </Tooltip>
              </MDBox>

              <MDBox px={0.2}>
                <Tooltip placement="top" title="Editar">
                  <Icon
                    style={style_icon}
                    onClick={() => handleClickEdit(original)}
                    fontSize="small"
                  >
                    edit
                  </Icon>
                </Tooltip>
              </MDBox>

              <MDBox px={0.2}>
                <Tooltip placement="top" title="Eliminar">
                  <Icon
                    style={style_icon}
                    onClick={(event) => handleClickErase(event, original)}
                    fontSize="small"
                  >
                    remove_circle
                  </Icon>
                </Tooltip>
              </MDBox>
            </MDBox>
          );
        },
      },
    ],
    rows: dataEntidades ?? [],
  };

  useRedirectForm(
    isLoadingG,
    null,
    isErrorG,
    errorG,
    "Hubo problemas para cargar la información",
    ENTIDADES_URL
  );

  useRedirectForm(
    isLoadingD,
    isSuccessD,
    isErrorD,
    errorD,
    "Entidad Eliminada",
    ENTIDADES_URL
  );

  useRedirectForm(
    isLoadingGId,
    null,
    isErrorGId,
    errorGId,
    "Hubo problemas para cargar la información",
    ENTIDADES_URL
  );

  const handleClickErase = (event, entidad) => {
    event.stopPropagation();

    Swal.fire({
      title: "¿Estás seguro?",
      text: `¡Si eliminas la entidad: "${entidad.nombre}", esta acción no se podrá revertir!`,
      icon: "warning",
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Eliminar",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteEntidad(entidad.id);
      }
    });
  };

  const navigate = useNavigate();

  const handleClickEdit = (entidad) => {
    dispatch(setNoHereAdmin(false));
    navigate(`${ENTIDAD_EDIT_URL}/${entidad.id}`);
    dispatch(setEditAdmin(entidad));
  };

  const handleClickCreate = () => {
    dispatch(setEditAdmin(null));
    dispatch(setNoHereAdmin(false));
  };

  const actions = (
    <MDBox display="flex" gap={2} alignItems="center">
      <Link to={ENTIDAD_NEW_URL}>
        <MDButton
          variant="contained"
          color="accent"
          size="medium"
          onClick={handleClickCreate}
        >
          nuevo
        </MDButton>
      </Link>
    </MDBox>
  );

  const handleClickMore = async (entidad) => {
    await getEntidadById(entidad.id)
      .unwrap()
      .then(() => {
        handleOpenShow();
      });
  };

  return (
    <MDBox pt={0} pb={3}>
      {entidad && (
        <ModalEntidadShow
          open={openShow}
          handleClose={handleCloseShow}
          entidad={entidad}
        />
      )}
      <MDBox display="flex" justifyContent={"center"} alignItems={"center"}>
        {isLoadingG ? (
          <CircularProgress sx={{ marginBottom: "3rem" }} color="inherit" />
        ) : (
          <DataTable
            tableHeader={tableHeader}
            actions={actions}
            table={dataTable}
            canSearch
            doubleClick={handleClickMore}
            paginationApi
            countTotal={countTotal}
            next={next}
            previous={previous}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            offset={offset}
            setOffset={setOffset}
            setBusqueda={setBusqueda}
          />
        )}
      </MDBox>
    </MDBox>
  );
}

DataTableEntidades.propTypes = {
  tableHeader: PropTypes.string.isRequired,
};

export default DataTableEntidades;
